import React, { useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  EditorView,
  keymap,
  placeholder as cmPlaceholder,
  lineNumbers,
  highlightActiveLineGutter,
} from '@codemirror/view';
import { EditorState, Compartment } from '@codemirror/state';
import {
  history,
  historyKeymap,
  defaultKeymap,
  undo,
  redo,
} from '@codemirror/commands';
import TxtToolbarButtons from './codemirror/toolbar/TxtToolbarButtons';

interface CodeMirrorFieldProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  autoFocus?: boolean;
  readOnly?: boolean;
  toolbarPortalTarget?: HTMLElement | null;
}

const CodeMirrorField: React.FC<CodeMirrorFieldProps> = ({
  value,
  onChange,
  placeholder = '',
  className = '',
  autoFocus = false,
  readOnly = false,
  toolbarPortalTarget,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  const prevReadOnlyRef = useRef(readOnly);
  // Flag to suppress onChange echo when we push a value into the editor externally
  const isSyncingRef = useRef(false);
  const editableCompartment = useRef(new Compartment());
  // Always call latest onChange without re-mounting the editor
  const onChangeRef = useRef(onChange);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  // Mount the editor once
  useEffect(() => {
    if (!containerRef.current) return;

    const comp = editableCompartment.current;

    const view = new EditorView({
      state: EditorState.create({
        doc: value,
        extensions: [
          history(),
          keymap.of([...defaultKeymap, ...historyKeymap]),
          lineNumbers(),
          highlightActiveLineGutter(),
          EditorView.lineWrapping,
          cmPlaceholder(placeholder),
          comp.of(EditorView.editable.of(!readOnly)),
          EditorView.updateListener.of((update) => {
            if (update.docChanged && !isSyncingRef.current) {
              onChangeRef.current(update.state.doc.toString());
            }
          }),
          EditorView.theme({
            '&': {
              height: '100%',
              backgroundColor: 'transparent',
              color: 'inherit',
            },
            '.cm-scroller': {
              fontFamily: "'JetBrains Mono', ui-monospace, 'Cascadia Mono', Menlo, Consolas, monospace",
              fontWeight: '400',
              fontSize: '0.875rem',
              lineHeight: '1.6',
            },
            '.cm-content': { padding: '12px' },
            '.cm-line': { padding: '0' },
            '&.cm-focused': { outline: 'none' },
            '.cm-placeholder': { color: '#9CA3AF' },
            '.cm-cursor': { borderLeftColor: 'currentColor' },
            '.cm-gutters': {
              backgroundColor: 'transparent',
              borderRight: '1px solid #e5e7eb',
              color: '#9ca3af',
            },
            '.cm-lineNumbers .cm-gutterElement': {
              padding: '0 8px',
              minWidth: '2.5em',
              textAlign: 'right',
            },
            '.cm-activeLineGutter': {
              backgroundColor: 'rgba(0,0,0,0.04)',
            },
          }),
        ],
      }),
      parent: containerRef.current,
    });

    viewRef.current = view;

    if (autoFocus && !readOnly) {
      requestAnimationFrame(() => view.focus());
    }

    return () => {
      view.destroy();
      viewRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // mount once

  // Sync value → editor on readOnly transitions (version preview / back to latest)
  // When readOnly is active, also sync if the value changes (async version load).
  // Never overwrite in-progress edits when readOnly is false.
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;

    const wasReadOnly = prevReadOnlyRef.current;
    prevReadOnlyRef.current = readOnly;

    // Reconfigure the editable compartment when readOnly changes
    if (wasReadOnly !== readOnly) {
      view.dispatch({
        effects: editableCompartment.current.reconfigure(
          EditorView.editable.of(!readOnly),
        ),
      });
    }

    // Push the value into the editor when viewing a version or returning from one
    if (readOnly || (wasReadOnly && !readOnly)) {
      const currentDoc = view.state.doc.toString();
      if (currentDoc !== value) {
        isSyncingRef.current = true;
        view.dispatch({
          changes: { from: 0, to: currentDoc.length, insert: value },
        });
        isSyncingRef.current = false;
      }
    }
  }, [value, readOnly]);

  const handleUndo = useCallback(() => {
    if (viewRef.current) undo(viewRef.current);
  }, []);

  const handleRedo = useCallback(() => {
    if (viewRef.current) redo(viewRef.current);
  }, []);

  const toolbar = <TxtToolbarButtons onUndo={handleUndo} onRedo={handleRedo} />;

  return (
    <div className={`flex-1 flex flex-col min-h-0 ${className}`}>
      {toolbarPortalTarget ? createPortal(toolbar, toolbarPortalTarget) : toolbar}
      <div ref={containerRef} className="flex-1 min-h-0 overflow-auto" />
    </div>
  );
};

export default CodeMirrorField;



